import { allOf, signalEquals } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * The leasing inquiry: call a prospect who does not have a home in mind yet.
 * Ask what they need, share the homes that match, and answer their questions.
 * Booking a showing is its own template, the showing scheduler. The agent
 * cannot transfer the call, so a request for a person goes to its own node,
 * which takes a message for the team.
 */
const START_ID = '00000000-0000-4000-8000-00000003d001';
const GREET_ID = '00000000-0000-4000-8000-00000003a005';
const FIND_ID = '00000000-0000-4000-8000-00000003c001';
const MESSAGE_ID = '00000000-0000-4000-8000-00000003c003';
const END_CLOSE_ID = '00000000-0000-4000-8000-00000003a002';
const END_DNC_ID = '00000000-0000-4000-8000-00000003a004';
const edgeId = (n) => `00000000-0000-4000-8000-00000003e${String(n).padStart(3, '0')}`;
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: edgeId(1),
    exitEdgeId: edgeId(9),
    toNodeId: GREET_ID,
});
export const LEASING_FOLLOW_UP_GRAPH_TEMPLATE = {
    name: 'Leasing inquiry',
    persona: 'You are Sam, a friendly assistant for Super. One or two short spoken ' +
        'sentences per turn.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [
        SCRATCHPAD_EVENTS.DO_NOT_CALL,
        SCRATCHPAD_EVENTS.NOT_INTERESTED,
        SCRATCHPAD_EVENTS.HANDOFF_REQUESTED,
    ],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: "Hi {{firstName}}, this is Sam with Super! I'm following up on your " +
                "rental inquiry, and I'd love to help you find the right home.",
        },
        {
            id: FIND_ID,
            type: 'conversation',
            title: 'Find a home',
            position: 2,
            prompt: 'Ask: "How many bedrooms are you looking for, and what monthly ' +
                'budget do you have in mind?"\n\n' +
                'Then share the homes that match, and answer their questions.\n\n' +
                'After each answer, ask if there is anything else you can help ' +
                'answer.',
            tools: [{ tool: 'GET_PROPERTY_LISTINGS' }, { tool: 'SWITCH_LANGUAGE' }],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            signals: [
                {
                    id: 'ready_to_wrap_up',
                    text: 'The caller indicates they have no further questions or they are ' +
                        'ready to move on or wrap up the conversation.',
                },
            ],
        },
        {
            id: MESSAGE_ID,
            type: 'conversation',
            title: 'Take a message',
            position: 3,
            prompt: 'Say you cannot transfer them right now, but you can take a ' +
                'message for the team to follow up. Take down their message and ' +
                'read it back briefly. Then ask: "Is there anything else I can help ' +
                'with?"',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            signals: [
                {
                    id: 'message_taken',
                    text: 'The caller gave their message for the team and said there is ' +
                        'nothing else',
                },
            ],
        },
        {
            id: END_CLOSE_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 4,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Thanks so much for your interest, {{firstName}}! We'll stay in " +
                    'touch. Have a great day!',
            },
        },
        {
            id: END_DNC_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update your contact so you don't get more calls " +
                    'from us. Have a good day.',
            },
        },
    ],
    edges: [
        OPENING.edges[0],
        // Cross-cutting flows, in priority order.
        {
            id: edgeId(3),
            fromNodeId: null,
            toNodeId: END_DNC_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.DO_NOT_CALL },
            position: 1,
        },
        {
            id: edgeId(4),
            fromNodeId: null,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 2,
        },
        {
            id: edgeId(5),
            fromNodeId: null,
            toNodeId: END_CLOSE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.NOT_INTERESTED },
            position: 3,
        },
        { ...OPENING.edges[1], position: 4 },
        {
            id: edgeId(2),
            fromNodeId: GREET_ID,
            toNodeId: FIND_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 5,
        },
        {
            id: edgeId(6),
            fromNodeId: FIND_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(FIND_ID, 'ready_to_wrap_up', true)),
            position: 6,
        },
        {
            id: edgeId(8),
            fromNodeId: MESSAGE_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(MESSAGE_ID, 'message_taken', true)),
            position: 7,
        },
    ],
};
//# sourceMappingURL=leasing-follow-up-template.js.map